import { useState, useEffect, useRef } from 'react'
import { X, Radio, CheckCircle, AlertCircle, Loader2, Keyboard } from 'lucide-react'
import { lookupByRfid, checkRfidExists } from '../services/api'

/**
 * RfidScanModal - Captures EID from Tru-Test stick readers in HID mode
 *
 * The reader acts as a Bluetooth keyboard and "types" the EID when scanned.
 * This modal provides an auto-focused input field to capture that input.
 *
 * Props:
 * - isOpen: boolean - whether modal is visible
 * - onClose: function - called when modal is closed
 * - onAnimalFound: function(animal) - called when existing animal is found
 * - onNewRfid: function(rfidTag) - called when RFID doesn't match any animal (for registration)
 * - onAnimalScanned: function(animal) - for continuous scan mode (stays open, resets input)
 * - scanCount: number - displayed in modal in continuous mode
 * - mode: 'lookup' | 'register' - determines behavior after scan
 *   - 'lookup': expects to find existing animal (for events)
 *   - 'register': expects new RFID (for registration)
 */
export default function RfidScanModal({
  isOpen,
  onClose,
  onAnimalFound,
  onNewRfid,
  onAnimalScanned,
  scanCount = 0,
  mode = 'lookup',
  ranchId
}) {
  const continuousMode = !!onAnimalScanned
  const [rfidInput, setRfidInput] = useState('')
  const [status, setStatus] = useState('ready') // ready, scanning, found, not_found, error, exists
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')
  const [manualMode, setManualMode] = useState(false)
  const inputRef = useRef(null)
  const debounceRef = useRef(null)

  // Focus the input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    }
    // Reset state when modal opens
    if (isOpen) {
      setRfidInput('')
      setStatus('ready')
      setResult(null)
      setError('')
    }
  }, [isOpen])

  // Auto-submit after input stops (debounced)
  // EID readers typically send the full number quickly, then stop
  useEffect(() => {
    if (rfidInput.length >= 10) { // Minimum EID length
      // Clear any existing debounce
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }

      // Wait 500ms after last input before processing
      debounceRef.current = setTimeout(() => {
        handleLookup(rfidInput)
      }, 500)
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [rfidInput])

  const handleLookup = async (tag) => {
    if (!tag.trim()) return

    setStatus('scanning')
    setError('')

    try {
      if (mode === 'register') {
        // For registration, check if tag already exists
        const response = await checkRfidExists(tag.trim(), ranchId)

        if (response.exists) {
          setStatus('exists')
          setResult(response)
          setError(`This RFID is already registered to "${response.animal_name}"`)
        } else {
          setStatus('not_found') // In register mode, not_found is good!
          // Proceed with registration
          if (onNewRfid) {
            onNewRfid(tag.trim())
          }
        }
      } else {
        // For lookup/events, find the animal
        const response = await lookupByRfid(tag.trim(), ranchId)

        if (response.found) {
          setStatus('found')
          setResult(response.animal)
          if (continuousMode) {
            // Continuous mode: notify parent and auto-reset for next scan
            onAnimalScanned(response.animal)
            setTimeout(() => {
              resetAndRetry()
            }, 1200)
          } else {
            // Single mode: close and navigate
            setTimeout(() => {
              if (onAnimalFound) {
                onAnimalFound(response.animal)
              }
            }, 800)
          }
        } else {
          setStatus('not_found')
          setError('No animal found with this RFID tag')
        }
      }
    } catch (err) {
      setStatus('error')
      setError(err.response?.data?.detail || 'Failed to look up RFID tag')
    }
  }

  const handleManualSubmit = (e) => {
    e.preventDefault()
    handleLookup(rfidInput)
  }

  const handleKeyDown = (e) => {
    // If Enter is pressed (some readers send Enter after the tag)
    if (e.key === 'Enter' && rfidInput.length >= 10) {
      e.preventDefault()
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      handleLookup(rfidInput)
    }
  }

  const resetAndRetry = () => {
    setRfidInput('')
    setStatus('ready')
    setResult(null)
    setError('')
    inputRef.current?.focus()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Radio className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">
                {continuousMode ? 'Bulk Scan' : mode === 'register' ? 'Scan New Animal' : 'Scan Animal'}
              </h2>
              <p className="text-sm text-gray-500">
                {continuousMode
                  ? `Scan animals one by one — ${scanCount} scanned`
                  : mode === 'register'
                  ? 'Scan EID to register new animal'
                  : 'Scan EID to find animal'
                }
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Status Display */}
          <div className="text-center mb-6">
            {status === 'ready' && (
              <div className="space-y-3">
                <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center animate-pulse">
                  <Radio className="h-8 w-8 text-blue-600" />
                </div>
                <p className="text-gray-600">Ready to scan...</p>
                <p className="text-sm text-gray-400">
                  Point EID reader at animal's tag
                </p>
              </div>
            )}

            {status === 'scanning' && (
              <div className="space-y-3">
                <div className="w-16 h-16 mx-auto bg-yellow-100 rounded-full flex items-center justify-center">
                  <Loader2 className="h-8 w-8 text-yellow-600 animate-spin" />
                </div>
                <p className="text-gray-600">Looking up tag...</p>
              </div>
            )}

            {status === 'found' && result && (
              <div className="space-y-3">
                <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <p className="text-green-600 font-semibold">Animal Found!</p>
                <div className="bg-green-50 rounded-lg p-4 text-left">
                  <p className="font-medium text-gray-900">
                    {result.local_name || result.rfid_tag}
                  </p>
                  <p className="text-sm text-gray-600">
                    {result.category} • {result.sex} • {result.breed_name || 'Unknown breed'}
                  </p>
                  {result.current_paddock && (
                    <p className="text-sm text-gray-500 mt-1">
                      Location: {result.current_paddock}
                    </p>
                  )}
                </div>
              </div>
            )}

            {(status === 'not_found' || status === 'exists') && (
              <div className="space-y-3">
                <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                </div>
                <p className="text-red-600 font-semibold">
                  {status === 'exists' ? 'Already Registered' : 'Not Found'}
                </p>
                {error && <p className="text-sm text-gray-600">{error}</p>}
                <button
                  onClick={resetAndRetry}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Scan again
                </button>
              </div>
            )}

            {status === 'error' && (
              <div className="space-y-3">
                <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                </div>
                <p className="text-red-600 font-semibold">Error</p>
                <p className="text-sm text-gray-600">{error}</p>
                <button
                  onClick={resetAndRetry}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Try again
                </button>
              </div>
            )}
          </div>

          {/* RFID Input Field */}
          <form onSubmit={handleManualSubmit}>
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={rfidInput}
                onChange={(e) => setRfidInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={manualMode ? "Enter RFID tag manually" : "Waiting for scan..."}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 text-center text-lg font-mono tracking-wider"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
              {rfidInput && (
                <button
                  type="button"
                  onClick={() => setRfidInput('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Manual entry toggle */}
            <div className="mt-4 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setManualMode(!manualMode)}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
              >
                <Keyboard className="h-4 w-4" />
                {manualMode ? 'Hide manual entry' : 'Enter manually'}
              </button>

              {manualMode && rfidInput.length >= 10 && (
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                >
                  Look Up
                </button>
              )}
            </div>
          </form>

          {/* Help text */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              <strong>Tip:</strong> Make sure your Tru-Test reader is in HID mode
              and paired to this device as a Bluetooth keyboard.
            </p>
          </div>

          {/* Done Scanning button for continuous mode */}
          {continuousMode && (
            <button
              onClick={onClose}
              className="mt-4 w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
            >
              Done Scanning ({scanCount} animals)
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
